import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '../store/store'
import { apiService } from '../services/api'
import { trackEvent } from '../lib/analytics'

// sweetalert2 is dynamically imported on first use so the ~165 KiB alerts
// vendor chunk stays out of the landing-page initial bundle.
const loadSwal = () => import('sweetalert2').then((m) => m.default)

interface AuditStatus {
  id: string
  title: string
  status: string
  risk_score?: number
}

interface AnalysisNotificationContextType {
  hasActiveAnalysis: boolean
}

const AnalysisNotificationContext = createContext<AnalysisNotificationContextType>({
  hasActiveAnalysis: false,
})

export const useAnalysisNotification = () => useContext(AnalysisNotificationContext)

/** Play a short notification chime using Web Audio API. */
function playNotificationSound() {
  try {
    const ctx = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)()
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()

    osc.connect(gain)
    gain.connect(ctx.destination)

    osc.type = 'sine'
    osc.frequency.setValueAtTime(587.33, ctx.currentTime) // D5
    osc.frequency.setValueAtTime(783.99, ctx.currentTime + 0.12) // G5
    osc.frequency.setValueAtTime(880, ctx.currentTime + 0.24) // A5

    gain.gain.setValueAtTime(0.3, ctx.currentTime)
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5)

    osc.start(ctx.currentTime)
    osc.stop(ctx.currentTime + 0.5)
  } catch {
    // Audio not available — silent fallback
  }
}

interface AnalysisNotificationProviderProps {
  children: React.ReactNode
  onViewAudit?: (auditId: string) => void
}

export const AnalysisNotificationProvider = ({ children, onViewAudit }: AnalysisNotificationProviderProps) => {
  const { isAuthenticated } = useSelector((state: RootState) => state.auth)
  const [hasActiveAnalysis, setHasActiveAnalysis] = useState(false)
  const previousStatusesRef = useRef<Map<string, string>>(new Map())
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const checkForCompletions = useCallback(async () => {
    try {
      const res = await apiService.getAudits(10)
      const audits: AuditStatus[] = res.data.audits || res.data.results || []

      const prevStatuses = previousStatusesRef.current
      const newStatuses = new Map<string, string>()
      let anyActive = false

      for (const audit of audits) {
        newStatuses.set(audit.id, audit.status)

        if (audit.status === 'in_progress') {
          anyActive = true
        }

        const prevStatus = prevStatuses.get(audit.id)
        if (!prevStatus) continue

        // Detect transition: in_progress → completed
        if (prevStatus === 'in_progress' && audit.status === 'completed') {
          trackEvent('audit_completed', {
            audit_id: audit.id,
            risk_score: Number(audit.risk_score || 0),
          })
          playNotificationSound()
          loadSwal().then((Swal) => {
            Swal.fire({
              icon: 'success',
              title: 'Analysis Complete',
              html: `<strong>${audit.title}</strong> analysis finished!<br/>Compliance Score: <strong>${Number(audit.risk_score || 0).toFixed(0)}%</strong>`,
              confirmButtonText: 'View Results',
              confirmButtonColor: '#4BC232',
              showCancelButton: true,
              cancelButtonText: 'Dismiss',
              cancelButtonColor: '#6b7280',
            }).then((result) => {
              if (result.isConfirmed && onViewAudit) {
                onViewAudit(audit.id)
              }
            })
          })
        }

        // Detect transition: in_progress → failed
        if (prevStatus === 'in_progress' && audit.status === 'failed') {
          trackEvent('audit_failed', { audit_id: audit.id })
          playNotificationSound()
          loadSwal().then((Swal) => {
            Swal.fire({
              icon: 'error',
              title: 'Analysis Failed',
              html: `<strong>${audit.title}</strong> analysis failed.<br/>Your credit has been refunded.`,
              confirmButtonText: 'View Details',
              confirmButtonColor: '#4BC232',
              showCancelButton: true,
              cancelButtonText: 'Dismiss',
              cancelButtonColor: '#6b7280',
            }).then((result) => {
              if (result.isConfirmed && onViewAudit) {
                onViewAudit(audit.id)
              }
            })
          })
        }
      }

      previousStatusesRef.current = newStatuses
      setHasActiveAnalysis(anyActive)
    } catch {
      // Silent — will retry on next poll
    }
  }, [onViewAudit])

  useEffect(() => {
    if (!isAuthenticated) {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current)
        pollIntervalRef.current = null
      }
      previousStatusesRef.current.clear()
      return
    }

    // Initial fetch to populate statuses (no notifications on first load)
    checkForCompletions()

    // Poll every 10 seconds
    pollIntervalRef.current = setInterval(checkForCompletions, 10000)

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current)
        pollIntervalRef.current = null
      }
    }
  }, [isAuthenticated, checkForCompletions])

  return (
    <AnalysisNotificationContext.Provider value={{ hasActiveAnalysis }}>
      {children}
    </AnalysisNotificationContext.Provider>
  )
}
